import * as React from "react";
import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableCell,
  TableRow,
  Typography,
  Grid,
  TextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";

const DMS_ARCHIVE_ENDPOINTS = {
  locations: "/admin/archive/dms/locations.json",
  text: "/admin/archive/dms/text.json",
};

const Component = (props) => {
  const [allData, setAllData] = React.useState(null);
  const [DMSLocationData, setDMSLocationData] = React.useState(null);
  const [selectedDate, setSelectedDate] = React.useState(null);
  const [selectedLocation, setSelectedLocation] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isLocationDataLoading, setIsLocationDataLoading] =
    React.useState(false);

  const convertDateISOToUTC = (isoDate) => {
    if (isoDate !== null && isoDate !== "") {
      const date = new Date(isoDate);
      const utcFormatted = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}-${String(date.getUTCDate()).padStart(2, "0")} ${String(date.getUTCHours()).padStart(2, "0")}:${String(date.getUTCMinutes()).padStart(2, "0")}:${String(date.getUTCSeconds()).padStart(2, "0")}`;
      return utcFormatted;
    } else return "";
  };

  const getDMSArchiveLocations = async () => {
    try {
      const res = await fetch(
        process.env.REACT_APP_API_HOST + DMS_ARCHIVE_ENDPOINTS.locations,
        {
          method: "GET",
        },
      );

      if (!res.ok) {
        throw new Error(`Error ${res.status}: ${res.statusText}`);
      }

      const data = await res.json();

      if (
        data.success === false &&
        data.error &&
        (data.error.code === "UNAUTHORIZED" || data.error.code === "FORBIDDEN")
      ) {
        setAllData({ error: data.error.code });
      } else {
        setAllData(data);
      }
    } catch (error) {
      console.error("Failed to fetch DMS archive locations:", error);
      if (error.message.indexOf("401") > -1) {
        setAllData({ error: "UNAUTHORIZED" });
      } else if (error.message.indexOf("403") > -1) {
        setAllData({ error: "FORBIDDEN" });
      } else {
        setAllData(null);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getDMSLocationIdData = React.useCallback(async () => {
    if (
      selectedDate !== null &&
      selectedDate !== "" &&
      selectedLocation !== null &&
      selectedLocation !== ""
    ) {
      setIsLocationDataLoading(true);
      try {
        const res = await fetch(
          process.env.REACT_APP_API_HOST +
            DMS_ARCHIVE_ENDPOINTS.text +
            "?fieldDeviceId=" +
            selectedLocation +
            "&date=" +
            selectedDate,
          {
            method: "GET",
          },
        );

        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }

        const data = await res.json();

        setDMSLocationData(data);
      } catch (error) {
        setDMSLocationData(null);
      } finally {
        setIsLocationDataLoading(false);
      }
    }
  }, [selectedDate, selectedLocation]);

  const renderDMSData = () => {
    if (!selectedLocation || !selectedDate) {
      return null;
    }

    if (DMSLocationData !== null && DMSLocationData !== undefined) {
      const dataToProcess = DMSLocationData?.data || DMSLocationData;

      let hasValidData = false;

      if (dataToProcess && typeof dataToProcess === "object") {
        try {
          Object.values(dataToProcess).forEach((eachDMSMessage) => {
            if (eachDMSMessage && typeof eachDMSMessage === "object") {
              Object.values(eachDMSMessage).forEach((eachInnerDMS) => {
                if (
                  eachInnerDMS?.messageSets &&
                  Object.keys(eachInnerDMS.messageSets).length > 0
                ) {
                  hasValidData = true;
                }
              });
            }
          });
        } catch (error) {}
      }

      if (!hasValidData) {
        return (
          <Box sx={{ mt: 2, textAlign: "center" }}>
            <Typography>
              {" "}
              No DMS data available for the selected location and date.
            </Typography>
          </Box>
        );
      }

      return (
        <Box sx={{ mt: 2 }}>
          <Table size="small" sx={{ textAlign: "center" }}>
            <TableRow className="report-header">
              <TableCell> Message </TableCell>
              <TableCell>Last Updated Time</TableCell>
            </TableRow>
            {Object.values(dataToProcess || {}).map(
              (eachDMSMessage, messageIdx) => {
                if (!eachDMSMessage || typeof eachDMSMessage !== "object")
                  return null;

                return Object.values(eachDMSMessage || {}).map(
                  (eachInnerDMS, idx) => {
                    if (!eachInnerDMS?.messageSets) return null;

                    return Object.values(eachInnerDMS.messageSets || {}).map(
                      (granDMSdata, dataIdx) => (
                        <TableRow
                          key={`${messageIdx}-${idx}-${dataIdx}`}
                          className={idx % 2 === 0 ? "styledtablerow" : ""}
                        >
                          <TableCell>
                            <Typography>
                              {granDMSdata?.lines ?? "No message"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography>
                              {convertDateISOToUTC(
                                granDMSdata?.lastUpdateTime,
                              ) ?? "No timestamp"}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ),
                    );
                  },
                );
              },
            )}
          </Table>
        </Box>
      );
    }
    return null;
  };

  const handleLocationChange = (e) => {
    setSelectedLocation(e.target.value);
  };

  const DMSArchiveLocations = React.useCallback(() => {
    if (isLoading) {
      return (
        <Box
          paddingTop={2}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mt: 4,
          }}
        >
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Loading DMS locations...</Typography>
        </Box>
      );
    }

    if (allData && allData.error === "UNAUTHORIZED") {
      const wasAdminLoggedIn = sessionStorage.getItem("isAdmin") === "true";
      const isUserLoggedIn = sessionStorage.getItem("isUser") === "true";
      const msg = isUserLoggedIn
        ? "You are not authorized to view this page."
        : null;
      return (
        <Box paddingTop={2}>
          <Typography variant="h5" sx={{ mb: 1, textAlign: "center", mt: 2 }}>
            {" "}
            {module.title}{" "}
          </Typography>
          <Typography sx={{ textAlign: "center", m: 2 }} variant="h6">
            {isUserLoggedIn ? (
              "You are not authorized to view this page."
            ) : wasAdminLoggedIn ? (
              <span>
                Session expired.{" "}
                <a
                  href={process.env.PUBLIC_URL + "/About/AdminLogin"}
                  className="hyper-links"
                >
                  <b>Click here to log back in</b>
                </a>
              </span>
            ) : (
              "Redirecting to login"
            )}
          </Typography>
        </Box>
      );
    }

    if (allData && allData.error === "FORBIDDEN") {
      return (
        <Box paddingTop={2}>
          <Typography variant="h5" sx={{ mb: 1, textAlign: "center", mt: 2 }}>
            {" "}
            {module.title}{" "}
          </Typography>
          <Typography sx={{ textAlign: "center", m: 2 }} variant="h5">
            Access denied - You don't have the required permissions to view this
            page.
          </Typography>
        </Box>
      );
    }

    const locations =
      allData?.locations || allData?.data?.locations || allData?.data;

    if (
      allData !== null &&
      allData !== undefined &&
      locations &&
      Array.isArray(locations)
    ) {
      return (
        <Box paddingTop={2}>
          <Typography variant="h5" sx={{ mb: 1, textAlign: "center", mt: 2 }}>
            {" "}
            {module.title}{" "}
          </Typography>
          <Typography>
            <DescriptionBlock />
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <FormControl
                sx={{
                  width: "35ch",
                  marginRight: 10,
                  marginLeft: 2,
                  marginBottom: 5,
                }}
              >
                <InputLabel id="DMSArchiveLocation-label">
                  {" "}
                  DMS Location{" "}
                </InputLabel>
                <Select
                  labelId="DMSArchiveLocation-label"
                  id="location-selection"
                  value={selectedLocation}
                  label="DMS Location"
                  onChange={handleLocationChange}
                  name="locations"
                >
                  {[...locations]
                    .sort((a, b) => a.location.localeCompare(b.location))
                    .map((eachLocation) => (
                      <MenuItem
                        value={eachLocation.fieldDeviceId}
                        key={eachLocation.fieldDeviceId}
                      >
                        {eachLocation.location}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              <FormControl
                sx={{ width: "35ch", marginRight: 10, marginBottom: 5 }}
              >
                <DatePicker
                  label="Start Date"
                  value={selectedDate}
                  onChange={(newValue) => setSelectedDate(newValue)}
                  renderInput={(params) => <TextField {...params} />}
                />
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      );
    } else {
      return (
        <Box paddingTop={2}>
          <Typography variant="h5" sx={{ mb: 1, textAlign: "center", mt: 2 }}>
            {" "}
            {module.title}{" "}
          </Typography>
          <Typography sx={{ textAlign: "center", m: 2 }}>
            Failed to load DMS locations
          </Typography>
        </Box>
      );
    }
  }, [allData, isLoading, selectedDate, selectedLocation]);

  React.useEffect(() => {
    getDMSArchiveLocations();
  }, []);

  React.useEffect(() => {
    DMSArchiveLocations();
  }, [allData, DMSArchiveLocations]);

  React.useEffect(() => {
    getDMSLocationIdData(selectedLocation, selectedDate);
  }, [selectedLocation, selectedDate, getDMSLocationIdData]);

  React.useEffect(() => {}, [DMSLocationData]);

  return (
    <Box>
      {DMSArchiveLocations()}
      {isLocationDataLoading ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mt: 4,
          }}
        >
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Loading DMS data...</Typography>
        </Box>
      ) : (
        renderDMSData()
      )}
    </Box>
  );
};

const DescriptionBlock = (props) => {
  return (
    <Typography variant="body1" sx={{ margin: 2, marginBottom: 2 }}>
      Select the location in the DMS field and choose a start date.
      <br></br>
      Use this page to generate a report of archived DMS data for a given date.
    </Typography>
  );
};

const module = {
  routeProps: {
    path: "DMSArchive",
    component: Component,
  },

  name: "DMS Archive",
  label: "DMS Archive",
  title: "Travel Midwest DMS Archive",
  showOnMenu: true,
};

export default module;
